import * as Api from "../../Api";
import * as AuthUtils from "../../lib/util/AuthUtils";
import * as NavigationUtils from "../../lib/util/NavigationUtils";
import * as GlobalActions from "../Global/Global.action";
import * as UserSelectors from "./User.selector";
import * as UserUtils from "../../lib/util/UserUtils";
import * as InsightsActions from "../Insights/Insights.action";
import * as StorageUtils from "../../lib/util/StorageUtils";
import * as AnalyticsUtils from "../../lib/util/AnalyticsUtils";
import * as SupportUtils from "../../lib/util/SupportUtils";
import * as ErrorUtils from "../../lib/util/ErrorUtils";
import * as Utils from "../../lib/util/Utils";
import * as FleetActions from "../Fleet/Fleet.action";
import * as Flags from "../../lib/Flags";

import store from "../index";
import { actionCreator, asyncActionCreators } from "../../lib/util/ReduxUtils";
import { Credential } from "../../lib/util/AuthUtils";
import { USER_ID_KEY, USER_OBJECT_KEY } from "../../StorageKeys";
import { User } from "../../lib/types/UserTypes";
import { isAuthRequiredForRoute } from "../../Routing";
import { MultiFactorDetails } from "../../lib/types/AuthTypes";
import * as FleetSelectors from "../Fleet/Fleet.selector";

export const addLoginRejection = actionCreator<void>(
	"[USER] ADD_LOGIN_REJECTION",
);

export const setMultiFactorDetails = actionCreator<MultiFactorDetails | null>(
	"[USER] SET_MULTI_FACTOR_DETAILS",
);

export const signOut = asyncActionCreators<boolean | void, boolean>(
	"[USER] SIGN_OUT",
	async (action) => {
		AuthUtils.resetEmulatedUserLevel();
		AuthUtils.removeAuthToken();
		if (isAuthRequiredForRoute(NavigationUtils.getCurrentRoute())) {
			await NavigationUtils.go({
				location: "/",
				includeRedirect: Boolean(action.payload),
			});
		}
		store.reset();
		return true;
	},
);

export const login = asyncActionCreators<
	{ email: string; password: string },
	User
>("[USER] LOGIN", async (action, dispatch, getState) => {
	dispatch(
		GlobalActions.updateLoadingOverlay({
			visible: true,
			message: "%login_submit_overlay",
		}),
	);

	const { email, password } = action.payload;

	try {
		const res = await Api.auth.submit({
			body: {
				username: email.trim(),
				password: password,
			},
		});
		const user = res.data.user;
		dispatch(loginSuccess(res.data));
		dispatch(setMultiFactorDetails(null));

		return user;
	} catch (error) {
		const isAuthError = ErrorUtils.isErrorCode(error, "auth/unsuccessful");

		if (isAuthError) {
			dispatch(addLoginRejection());
		} else {
			try {
				AnalyticsUtils.logError(error, false);
				// eslint-disable-next-line no-empty
			} catch (e) {}
		}
		const isMultiFactorError = ErrorUtils.isErrorCode(
			error,
			"auth/multi-factor-auth-required",
		);
		if (isMultiFactorError) {
			dispatch(setMultiFactorDetails(ErrorUtils.parseError(error).details));
		}

		dispatch(GlobalActions.updateLoadingOverlay({ visible: false }));

		throw error;
	}
});

export const updateUserObject = actionCreator<Partial<User> | null>(
	"[USER] UPDATE_USER_OBJECT",
	(action, dispatch, getState) => {
		const state = getState();
		const fleet = FleetSelectors.selectCurrentScopeFleet(state);
		const currentUser = UserSelectors.selectLoggedInUser(state);

		const user: User = {
			...currentUser,
			...action.payload,
		} as any;

		if (fleet.rejected && user.group) {
			// Kick user back into their top level group
			dispatch(
				GlobalActions.setScope({
					fleetId: user.fleetId,
					group: user.group,
					rootGroupId: user.rootGroup,
				}),
			);
		}

		const currentFleetId = UserSelectors.selectUserScopeFleetId(user);

		if (UserUtils.isGAEnabled(user)) {
			AnalyticsUtils.initialiseGA();
		} else {
			AnalyticsUtils.blockGA();
		}

		if (Utils.deepEqual(user, currentUser)) {
			action.payload = null;
			return action;
		}

		if (
			currentFleetId &&
			UserUtils.isInsightsEnabled(user) &&
			!UserUtils.isInsightsEnabled(currentUser)
		) {
			dispatch(
				InsightsActions.getInsights({
					fleetId: currentFleetId,
					group: UserSelectors.selectCurrentScopeGroupId(state),
					userId: user?.uid,
				}),
			);
		}

		StorageUtils.set(USER_OBJECT_KEY, {
			...user,
			fleetIdOverride: undefined,
		});
		AnalyticsUtils.userChange(user);
		return action;
	},
);

export const updateUserOnboardingStep = actionCreator<any>(
	"[USER] UPDATE_USER_ONBOARDING_STEP",
	(action, dispatch, getState) => {
		const { step, skipAPI, reset, resetTerms } = action.payload;

		const state = getState();
		const user = UserSelectors.selectLoggedInUser(state);
		if (user) {
			let data: any = {
				portalOnboarding: { ...user.portalOnboarding, ...step },
			};

			if (reset) {
				data = { portalOnboarding: { reset: true } };
			}

			if (resetTerms) {
				data.portalTermsVersionAccepted = 0;
			}

			dispatch(updateUserObject(data));

			if (!skipAPI && !user.portalOnboarding.reset) {
				Api.postUserUpdate.submit({
					params: { userId: user.uid },
					body: data,
				});
			}
		}
	},
);

export const loginSuccess = actionCreator<Credential>(
	"[USER] LOGIN_SUCCESS",
	(action, dispatch, getState) => {
		const auth = action.payload;

		const { user } = auth;

		Api.API_DEF.clearCache();

		Utils.nextFrame(async () => {
			await dispatch(updateAuthInfo(auth));

			// redirect user
			const redirectURL = NavigationUtils.getRedirectLocation() || "/home";
			// eslint-disable-next-line
			NavigationUtils.go(redirectURL).then(
				(data) => {
					dispatch(GlobalActions.updateLoadingOverlay({ visible: false }));
					return data;
				},
				(error) => {
					throw error;
				},
			);

			if (!AuthUtils.isMultiFactorMandatedButNotEnabled()) {
				dispatch(refreshUserObject({ keepAuth: true }));
				dispatch(FleetActions.getAllFleets.refresh());
				dispatch(
					GlobalActions.setScope({
						fleetId: null,
						group: user.group,
						rootGroupId: user.rootGroup,
					}),
				);
			}
		});
	},
);

export const updateAuthInfo = actionCreator<Credential>(
	"[USER] UPDATE_AUTH_INFO",
	(action, dispatch, getState) => {
		const auth = action.payload;
		AuthUtils.setAuthToken(auth);
		StorageUtils.set(USER_OBJECT_KEY, auth.user);
		dispatch(updateUserObject(auth.user));
	},
);

export const refreshUserObject = asyncActionCreators<
	{ keepAuth: boolean },
	User | undefined | null
>("[USER] REFRESH_USER_OBJECT", async (action, dispatch, getState) => {
	const { keepAuth } = action.payload;

	try {
		const userId = StorageUtils.get(USER_ID_KEY);
		if (userId) {
			if (Flags.DEMO_MODE.is()) {
				return AuthUtils.getStoredUserObject();
			}
			const res = await Api.fetchCurrentUser.submit({
				query: {},
				options: {
					lock: false,
					cache: false,
				},
			});

			// make sure we clear out group if it was removed backend
			dispatch(
				updateUserObject({ group: null, rootGroup: null, ...res.data } as any),
			);
			SupportUtils.setHubspotIdentity(res.data);

			return res.data;
		}
		return null;
	} catch (error) {
		dispatch(signOut(true));
		throw error;
	}
});
